import React, { useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Layout, Breadcrumb, Avatar, Image } from 'antd';
import { makeStyles } from '@material-ui/core';
import ArrowNextIcon from '../../../asset/arrow-next.svg';
import CButton from '../../../component/Button';
import Loader from '../../../component/Loader/Loader';
import NoxTable from '../../../component/Table';
import InnerHeader from '../../../component/InnerHeader';
import formatDate from '../../../helpers/formatDate';
import { ReactComponent as Star } from '../../../asset/star-border.svg';
import { fetchAllOrders } from '../../../actions/ordersAction';
import { fetchReviews, removeReview } from '../../../actions/ReviewAction';

const useStyles = makeStyles({
  gridContainer: {
    display: 'grid',
    gridRowGap: '25px',
    gridTemplateColumns: '33% 33% 33%',
    marginLeft: '90px',
    marginTop: '55px'
  },
  gridHeader: {
    display: 'grid',
    gridRowGap: '25px',
    gridTemplateColumns: '33% 33% 33%',
    marginLeft: '90px',
    textAlign: 'start',
    width: '100%'
  },
  gridItem: {
    maxWidth: '90%',
    textAlign: 'start',
    wordWrap: 'break-word'
  },
  gridItemPayment: {
    '& div': {
      marginBottom: '20px'
    },
    gridColumn: '1',
    gridRow: 'span 2',
    height: '100%',
    maxWidth: '90%',
    textAlign: 'start'
  },
  gridItemPayment2: {
    '& div': {
      marginBottom: '20px'
    },
    gridColumn: '2',
    gridRow: 'span 1',
    height: '100%',
    maxWidth: '90%',
    textAlign: 'start'
  },
  gridItemPayment3: {
    '& div': {
      marginBottom: '20px'
    },
    gridColumn: '3',
    gridRow: 'span 1',
    height: '100%',
    maxWidth: '90%',
    textAlign: 'start'
  },
  p: {
    color: '#9BA0AD',
    fontFamily: 'Poppins',
    fontSize: '12px',
    fontStyle: 'normal',
    fontWeight: 'normal',
    lineHeight: '100%'
  },
  span: {
    fontFamily: 'Poppins',
    fontSize: '18px',
    fontStyle: 'normal',
    fontWeight: 500
  },
  svg: {
    '& path': {
      stroke: '#CAD8FF'
    },
    marginRight: '6px'
  }
});

const ReviewsDetail = () => {
  const { review } = useParams();
  const dispatch = useDispatch();
  const history = useHistory();
  const classes = useStyles();
  const currentProject = useSelector(({ project }) => project.currentProject);
  const orders = useSelector(({ order }) => order.orders);
  const reviews = useSelector(({ review }) => review.reviews);
  const selectedOrder = orders.find((item) => item._id === review);

  useEffect(() => {
    async function load() {
      dispatch(fetchAllOrders());
    }

    if (currentProject) {
      load();
    }
  }, [currentProject, dispatch]);

  function itemRender(route, params, routes) {
    const last = routes.indexOf(route) === routes?.length - 1;

    return last ? (
      <span>{route?.breadcrumbName}</span>
    ) : (
      <span onClick={() => history.goBack()} style={{ cursor: 'pointer' }}>
        {route?.breadcrumbName}
      </span>
    );
  }

  const routes = [
    {
      breadcrumbName: 'Reviews',
      path: '/ecommerce/reviews'
    },
    {
      breadcrumbName: review,
      path: `/ecommerce/reviews/${review}`
    }
  ];

  const mapToTable = (orders) => {
    const data = [];

    if (!orders || !orders?.length) {
      return data;
    }

    orders.forEach(({ itemId, selectedOption, quantity, item }, index) => {
      data.push({
        color: selectedOption?.color ?? '-',
        id: itemId,
        key: index,
        link: item?.imageUrl ?? '-',
        price: `$${item?.price}` ?? '-',
        qty: quantity,
        size: selectedOption?.size ?? '-',
        title: item?.title ?? '-',
        weight: item?.weight ?? '-'
      });
    });

    return data;
  };

  const data = mapToTable(selectedOrder?.selectedItems);

  const columns = [
    {
      dataIndex: 'link',
      key: 'link',
      render: (link) => (
        <Image height={40} src={link} style={{ objectFit: 'contain' }} width={40} />
      ),
      title: () => {
        return <div style={{ textAlign: 'center' }}>Preview</div>;
      }
    },
    {
      dataIndex: 'title',
      key: 'title',
      sorter: (a, b) => a.title.localeCompare(b.title),
      title: 'Name'
    },

    {
      dataIndex: 'price',
      key: 'price',
      sorter: (a, b) => a.price.localeCompare(b.price),
      title: 'Price'
    },
    {
      dataIndex: 'id',
      key: 'id',
      sorter: (a, b) => a.id.localeCompare(b.id),
      title: 'Item ID'
    },
    {
      dataIndex: 'color',
      key: 'color',
      sorter: (a, b) => a.color.localeCompare(b.color),
      title: 'Color'
    },
    {
      dataIndex: 'size',
      key: 'size',
      sorter: (a, b) => a.size.localeCompare(b.size),
      title: 'Size'
    },
    {
      dataIndex: 'qty',
      key: 'qty',
      sorter: (a, b) => a.qty.localeCompare(b.qty),
      title: 'Qty'
    },
    {
      dataIndex: 'weight',
      key: 'weight',
      sorter: (a, b) => a.weight.localeCompare(b.weight),
      title: 'Item Weight'
    }
  ];

  const onReviewDelete = () => {
    dispatch(
      removeReview(currentProject._id, review, () => {
        dispatch(fetchReviews());
      })
    );
    history.goBack();
  };

  const OrderHeader = () => {
    const selectedCustomer = reviews.find((item) => item.orderId === review);
    const finalDate = formatDate(selectedCustomer?.createdAt);
    const userName = `@${selectedCustomer?.profile?.userName}`;
    const startsArray = Array.from(Array(5).keys());

    return (
      <div
        style={{
          display: 'flex',
          width: '100%'
        }}
      >
        <div className={classes.gridHeader}>
          <div className={classes.gridItem} style={{ position: 'relative' }}>
            <Avatar
              src={selectedCustomer?.profile?.profilePictureUrl}
              style={{
                display: 'flex',
                height: '55px',
                left: '-68px',
                position: 'absolute',
                width: '55px'
              }}
            />
            <span className={classes.span}>{userName || '-'}</span>
            <p className={classes.p} style={{ marginTop: '3px' }}>
              ID: <span style={{ color: 'black' }}>{selectedCustomer?.profile.userId || '-'}</span>
            </p>
          </div>
          <div className={classes.gridItem}>
            <span className={classes.span}>{finalDate || '-'}</span>
            <p className={classes.p}>Date & Time</p>
          </div>
          <div className={classes.gridItem} style={{ display: 'flex' }}>
            {startsArray.map((index) => {
              if (selectedCustomer?.rating > index) {
                return <Star key={index} className={classes.svg} fill="#CAD8FF" />;
              }
              return <Star key={index} className={classes.svg} />;
            })}
            <span style={{ fontSize: '24px', marginLeft: '15px' }}>
              {`${selectedCustomer?.rating || '-'}/5`}
            </span>
          </div>
        </div>
      </div>
    );
  };

  const OrderBody = () => {
    const selectedCustomer = reviews.find((item) => item.orderId === review);

    return (
      <div className={classes.gridContainer}>
        <div className={classes.gridItemPayment}>
          <div className={classes.gridItem}>
            <p className={classes.p}>Buyer</p>
            <span className={classes.span}>{'-' || '-'}</span>
          </div>
          <div className={classes.gridItem}>
            <p className={classes.p}>Buyer ID</p>
            <span className={classes.span}>{selectedCustomer?.profile?.userId || '-'}</span>
          </div>
          <div className={classes.gridItem}>
            <p className={classes.p}>Seller</p>
            <span className={classes.span}>{'-' || '-'}</span>
          </div>
          <div className={classes.gridItem}>
            <p className={classes.p}>Seller Id</p>
            <span className={classes.span}>{selectedCustomer?.sellerId || '-'}</span>
          </div>
        </div>

        <div className={classes.gridItemPayment2}>
          <div className={classes.gridItem}>
            <p className={classes.p}>Order ID</p>
            <span className={classes.span}>{selectedCustomer?.orderId || '-'}</span>
          </div>
          <div className={classes.gridItem}>
            <p className={classes.p}>Order Date</p>
            <span className={classes.span}>{'-' || '-'}</span>
          </div>
          <div className={classes.gridItem}>
            <p className={classes.p}>Total Price</p>
            <span className={classes.span}>-</span>
          </div>
        </div>

        <div className={classes.gridItemPayment3}>
          <div className={classes.gridItem}>
            <p className={classes.p}>Review ID</p>
            <span className={classes.span}>{'-' || '-'}</span>
          </div>
          <div className={classes.gridItem}>
            <p className={classes.p}>Review</p>
            <span className={classes.span}>{selectedCustomer?.text || '-'}</span>
          </div>
        </div>
      </div>
    );
  };

  const isLoaded = useSelector(({ review }) => review.reviews);

  if (!isLoaded) {
    return <Loader />;
  }

  return (
    <Layout style={{ background: 'white' }}>
      <div
        style={{
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'space-between',
          padding: '35px 40px 0px 35px'
        }}
      >
        <Breadcrumb
          itemRender={itemRender}
          routes={routes}
          separator={<img alt="separator icon" src={ArrowNextIcon} />}
          style={{
            background: 'white',
            display: 'flex',
            justifyContent: 'flex-start'
          }}
        />
        <div>
          <CButton onClick={onReviewDelete} type="primary">
            Delete
          </CButton>
        </div>
      </div>
      <div style={{ marginTop: '55px', paddingLeft: '35px' }}>
        <OrderHeader />
        <OrderBody />
      </div>
      <div
        style={{
          height: 'calc(100vh - 165px)',
          marginTop: '80px',
          overflowY: 'auto'
        }}
      >
        <InnerHeader subtitle={selectedOrder?.selectedItems?.length} title="Items" />
        <NoxTable checkbox={false} columns={columns} data={data} searchBy="title" />
      </div>
    </Layout>
  );
};

export default ReviewsDetail;
